import { useState, useEffect, type ReactNode } from 'react'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card } from './Card'

export interface ExplainerCardProps {
  /** Scopes the localStorage key: `explainer-${id}` */
  id: string
  /** Emoji string or Lucide icon element rendered before the title */
  icon: ReactNode
  title: string
  /** Short summary always visible below the title */
  body: string
  /** Detailed content revealed when expanded */
  children?: ReactNode
  /** Overrides localStorage on first render only (when no stored preference exists) */
  defaultOpen?: boolean
  className?: string
  /**
   * `'default'` — the original collapsible `bg-base-200` card with a plain
   * inline icon + a "why this matters" toggle (unchanged; every existing
   * call site keeps its exact prior render).
   *
   * `'info'` (EP-114 T010) — a non-collapsible info banner: `bg-fd-blue`
   * container, an icon rendered inside a `bg-primary` chip, and a bold 20px
   * title. Used above the wizard step-2 pricing columns. Never shows the
   * "why this matters" toggle, regardless of `children`.
   */
  variant?: 'default' | 'info'
}

const STORAGE_KEY_PREFIX = 'explainer-'

function readStoredState(id: string): 'expanded' | 'collapsed' | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${id}`)
    if (raw === 'expanded') return 'expanded'
    if (raw === 'collapsed') return 'collapsed'
    return null
  } catch {
    return null
  }
}

function writeStoredState(id: string, expanded: boolean): void {
  try {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${id}`,
      expanded ? 'expanded' : 'collapsed',
    )
  } catch {
    // Ignore storage errors (e.g. private browsing quota exceeded)
  }
}

export function ExplainerCard({
  id,
  icon,
  title,
  body,
  children,
  defaultOpen = false,
  className = '',
  variant = 'default',
}: ExplainerCardProps) {
  const { t } = useTranslation('common')

  // Initialise from localStorage; fall back to defaultOpen
  const [expanded, setExpanded] = useState<boolean>(() => {
    const stored = readStoredState(id)
    if (stored === 'expanded') return true
    if (stored === 'collapsed') return false
    return defaultOpen
  })

  // Re-sync when id changes (e.g. navigating between wizard steps)
  useEffect(() => {
    const stored = readStoredState(id)
    if (stored === 'expanded') {
      setExpanded(true)
    } else if (stored === 'collapsed') {
      setExpanded(false)
    } else {
      setExpanded(defaultOpen)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  function toggle() {
    const next = !expanded
    setExpanded(next)
    writeStoredState(id, next)
  }

  const bodyId = `explainer-body-${id}`

  // 'info' variant (EP-114 T010): a plain, non-collapsible banner. Bypasses
  // the localStorage expand/collapse state entirely — it never renders a
  // toggle, so `expanded`/`children` are irrelevant here.
  if (variant === 'info') {
    return (
      <div
        className={`bg-fd-blue border border-fd-border rounded-xl p-4 flex flex-col gap-2 ${className}`}
        data-testid={`explainer-card-${id}`}
      >
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="flex-shrink-0 inline-flex items-center justify-center bg-primary text-primary-content rounded-2xl p-1"
          >
            {icon}
          </span>
          <span className="font-bold text-xl text-fd-grey-darker">{title}</span>
        </div>
        <p className="text-sm text-base-content leading-[1.5]">{body}</p>
      </div>
    )
  }

  return (
    <Card
      noBody
      className={`card-compact bg-base-200 shadow-none p-3 md:p-4 ${className}`}
      data-testid={`explainer-card-${id}`}
    >
      {/* Icon + title row */}
      <div className="flex items-center gap-2 mb-1">
        <span aria-hidden="true" className="flex-shrink-0 text-base leading-none">
          {icon}
        </span>
        <span className="font-semibold text-base-content text-sm md:text-base">{title}</span>
      </div>

      {/* Short body — always visible */}
      <p className="text-sm text-base-content/70 mb-2">{body}</p>

      {/* Toggle button — only render when there's an expanded body to show.
          Wizard step explainers pass no children, so the button used to
          render but did nothing (user feedback: "not usable"). */}
      {children && (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={bodyId}
          className="flex items-center gap-1 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded transition-colors w-fit"
        >
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
          )}
          {t('actions.why_this_matters')}
        </button>
      )}

      {/* Expanded detail — conditionally rendered (no children → no toggle content) */}
      {expanded && children && (
        <div
          id={bodyId}
          className="mt-3 border-l-4 border-primary pl-4 text-sm text-base-content/80"
        >
          {children}
        </div>
      )}

      {/* Keep aria target in DOM when there are no children */}
      {(!expanded || !children) && <div id={bodyId} hidden />}
    </Card>
  )
}
